import React, { useMemo } from 'react';
import { DndContext, PointerSensor, useSensor, useSensors, DragEndEvent, closestCenter } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';

export interface EditorTabItem {
  id: string;
  name: string;
  path: string;
  isDirty?: boolean;
  isPinned?: boolean;
}

type EditorTabBarVariant = 'default' | 'pill';

interface EditorTabBarProps {
  tabs: EditorTabItem[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onClose: (id: string) => void;
  onReorder: (orderedIds: string[]) => void;
  onNewUntitled: () => void;

  onCloseOthers: (id: string) => void;
  onCloseRight: (id: string) => void;
  onSave: (id: string) => void;
  onSaveAll: () => void;
  onRevealInExplorer: (id: string) => void;
  onCopyPath: (id: string) => void;
  onPopOut?: (id: string) => void;
  showSaveItems?: boolean;
  showRevealItems?: boolean;
  variant?: EditorTabBarVariant;
}

const SortableTab: React.FC<{
  tab: EditorTabItem;
  isActive: boolean;
  onSelect: (id: string) => void;
  onClose: (id: string) => void;
  onCloseOthers: (id: string) => void;
  onCloseRight: (id: string) => void;
  onSave: (id: string) => void;
  onSaveAll: () => void;
  onRevealInExplorer: (id: string) => void;
  onCopyPath: (id: string) => void;
  onPopOut?: (id: string) => void;
  showSaveItems: boolean;
  showRevealItems: boolean;
  variant: EditorTabBarVariant;
}> = ({
  tab,
  isActive,
  onSelect,
  onClose,
  onCloseOthers,
  onCloseRight,
  onSave,
  onSaveAll,
  onRevealInExplorer,
  onCopyPath,
  onPopOut,
  showSaveItems,
  showRevealItems,
  variant,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: tab.id,
    disabled: tab.isPinned,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isPill = variant === 'pill';
  const canClose = !tab.isPinned;
  const canPopOut = !tab.isPinned && Boolean(onPopOut);
  const canShowSaveItems = showSaveItems && !tab.isPinned;
  const canShowRevealItems = showRevealItems && !tab.isPinned;
  const hasContextMenuActions = canClose || canPopOut || canShowSaveItems || canShowRevealItems;

  const tabContent = (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'group flex select-none items-center gap-2',
        isPill
          ? [
              'h-10 rounded-full border px-4 text-sm transition-all',
              canClose ? 'pr-2' : 'pr-4',
              isActive
                ? 'border-border bg-background shadow-sm shadow-black/5'
                : 'border-transparent bg-transparent text-muted-foreground hover:bg-background/75 hover:text-foreground',
            ]
          : [
              'h-9 rounded-t border-b-2 border-transparent pl-3',
              canClose ? 'pr-1' : 'pr-3',
              isActive ? 'border-b-primary bg-background' : 'bg-muted/50 hover:bg-accent/60',
            ],
        isDragging ? 'opacity-80' : ''
      )}
      onClick={() => onSelect(tab.id)}
      onMouseDown={(e) => {
        if (canClose && e.button === 1) {
          e.preventDefault();
          onClose(tab.id);
        }
      }}
      {...attributes}
      {...listeners}
      title={tab.path || tab.name}
    >
      {tab.isDirty && (
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden />
      )}
      <span className={cn('text-sm', isPill ? (isActive ? 'text-foreground' : 'text-inherit') : 'text-foreground')}>
        {tab.name || 'Untitled'}
      </span>

      {canClose && (
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            'ml-1 p-0 hover:bg-foreground/10',
            isPill ? 'h-7 w-7 rounded-full' : 'h-6 w-6',
            isActive || isPill ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
          )}
          aria-label={`Close ${tab.name}`}
          onClick={(e) => {
            e.stopPropagation();
            onClose(tab.id);
          }}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );

  if (!hasContextMenuActions) {
    return tabContent;
  }

  return (
    <ContextMenu>
      <ContextMenuTrigger id={`trigger-${tab.id}`} asChild>
        {tabContent}
      </ContextMenuTrigger>
      <ContextMenuContent className="w-52">
        {canShowSaveItems && (
          <>
            <ContextMenuItem onClick={() => onSave(tab.id)}>Save</ContextMenuItem>
            <ContextMenuItem onClick={onSaveAll}>Save All</ContextMenuItem>
          </>
        )}

        {canShowSaveItems && (canPopOut || canClose || canShowRevealItems) && <ContextMenuSeparator />}

        {canPopOut && (
          <ContextMenuItem onClick={() => onPopOut?.(tab.id)}>Pop out to window</ContextMenuItem>
        )}

        {canPopOut && canClose && <ContextMenuSeparator />}

        {canClose && (
          <>
            <ContextMenuItem onClick={() => onClose(tab.id)}>Close</ContextMenuItem>
            <ContextMenuItem onClick={() => onCloseOthers(tab.id)}>Close Others</ContextMenuItem>
            <ContextMenuItem onClick={() => onCloseRight(tab.id)}>Close to the Right</ContextMenuItem>
          </>
        )}

        {(canShowSaveItems || canPopOut || canClose) && canShowRevealItems && <ContextMenuSeparator />}

        {canShowRevealItems && (
          <>
            <ContextMenuItem onClick={() => onRevealInExplorer(tab.id)}>
              Reveal in Explorer
            </ContextMenuItem>
            <ContextMenuItem onClick={() => onCopyPath(tab.id)}>Copy Path</ContextMenuItem>
          </>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
};

const EditorTabBar: React.FC<EditorTabBarProps> = ({
  tabs,
  activeId,
  onSelect,
  onClose,
  onReorder,
  onNewUntitled,
  onCloseOthers,
  onCloseRight,
  onSave,
  onSaveAll,
  onRevealInExplorer,
  onCopyPath,
  onPopOut,
  showSaveItems = true,
  showRevealItems = true,
  variant = 'default',
}) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = tabs.findIndex((tab) => tab.id === active.id);
    const newIndex = tabs.findIndex((tab) => tab.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const newOrder = arrayMove(tabs, oldIndex, newIndex).map((tab) => tab.id);
    onReorder(newOrder);
  };

  const tabIds = useMemo(() => tabs.map((tab) => tab.id), [tabs]);
  const isPill = variant === 'pill';

  return (
    <div className={cn('w-full', isPill ? 'border-0 bg-transparent' : 'border-b border-border bg-muted/40')}>
      <div
        className={cn(
          'flex overflow-x-auto no-scrollbar',
          isPill ? 'items-center' : 'items-end'
        )}
        onDoubleClick={() => onNewUntitled()}
        role="tablist"
        aria-label="Editor Tabs"
      >
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={tabIds} strategy={horizontalListSortingStrategy}>
            <div className={cn('flex', isPill ? 'items-center gap-2 px-1' : 'items-end gap-1 pl-1')}>
              {tabs.map((tab) => (
                <SortableTab
                  key={tab.id}
                  tab={tab}
                  isActive={tab.id === activeId}
                  onSelect={onSelect}
                  onClose={onClose}
                  onCloseOthers={onCloseOthers}
                  onCloseRight={onCloseRight}
                  onSave={onSave}
                  onSaveAll={onSaveAll}
                  onRevealInExplorer={onRevealInExplorer}
                  onCopyPath={onCopyPath}
                  onPopOut={onPopOut}
                  showSaveItems={showSaveItems}
                  showRevealItems={showRevealItems}
                  variant={variant}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
        <div className="flex-1" />
      </div>
    </div>
  );
};

export default EditorTabBar;